/**
 * departmentResolution — single source of truth for the visit-registration
 * provider↔Department linkage (EPIC A / A2, stories S7 + S8).
 *
 * The visit's `department_id` is STAMPED at registration and frozen. The
 * dept-of-care derives from the SELECTED ACTIVE ATTENDING PROVIDER's departments
 * (deptsByMember[activeProviderId]) — NOT the logged-in/registering user. The
 * ACTIVE PROVIDER is whichever provider the visit type keys on: the DOCTOR for an
 * OPD visit (S7), the NURSE for a Nurse visit, or the LAB TECHNICIAN for a Lab
 * visit (S8). Nurses and lab techs are org members with dept memberships exactly
 * like doctors, so ONE role-agnostic resolver serves all three. The provider
 * list, in turn, filters to the providers that belong to a department once one
 * is picked first. This bidirectional coupling lives here so the render gate, the
 * auto-fill effect, the provider filter, the re-derivation on provider change and
 * the submit validation all read ONE util and can't drift.
 *
 * Rules:
 *   - Org has 0 active departments  → NO picker; department_id omitted
 *     (graceful degrade — pre-A2 flow untouched).
 *   - Visit type == EMERGENCY       → NO picker; server stamps the EMERGENCY
 *     sentinel (org-wide). Requirement satisfied server-side.
 *   - Org has departments, non-emergency:
 *       · the active provider is selected with exactly ONE active dept →
 *         AUTO-FILL that dept + LOCK the picker (read-only). (AC2)
 *       · the active provider is selected with MULTIPLE active depts → picker
 *         CONSTRAINED to that provider's depts, no default, required. (AC3)
 *       · the active provider is selected with NO dept → picker offers the FULL
 *         active org dept list, manual pick required. (AC4)
 *       · NO active provider selected yet → picker offers the FULL active org
 *         list, manual pick required. (AC10)
 *
 * @param {object}   args
 * @param {Array}    args.departments            org dept list (departmentStore) — [{dept_id/id, name, active}]
 * @param {Array}    args.activeProviderDepts    the SELECTED active provider's dept edges (deptsByMember[providerId]) — [{dept_id, name}] | null
 * @param {boolean}  args.hasActiveProvider      whether an active attending provider (doctor/nurse/lab tech) is selected.
 * @param {Array}    [args.selectedDoctorDepts]  S7 back-compat alias for activeProviderDepts.
 * @param {boolean}  [args.hasDoctor]            S7 back-compat alias for hasActiveProvider.
 * @param {string}   args.visitType              selected visit type (e.g. 'REGULAR' | 'EMERGENCY')
 * @returns {{
 *   showPicker: boolean,
 *   required: boolean,
 *   locked: boolean,
 *   autoFillDeptId: string|null,
 *   options: Array,            // the dept options to render ([{dept_id, name}])
 *   isEmergency: boolean,
 *   hasDepartments: boolean,
 * }}
 */
/**
 * Sentinel department_id the server stamps on EMERGENCY visits (org-wide, not a
 * real department). MUST match the backend `EMERGENCY_DEPT_SENTINEL`
 * (app/schemas/department.py = "EMERGENCY") so an emergency visit's anchor is
 * short-circuited to "no filter" rather than mis-scoped to a phantom dept.
 */
export const EMERGENCY_DEPT_SENTINEL = 'EMERGENCY';

/**
 * True for an EMERGENCY (casualty) visit type. Single source for the form: the
 * department picker hides for it (server stamps the sentinel) and the doctor
 * picker asks for the org-wide roster (admission-access R2 / bk-1733).
 */
export const isEmergencyVisitType = (visitType) => String(visitType || '').toUpperCase() === 'EMERGENCY';

/**
 * A4 / S3 — the department ANCHOR for the add-procedure catalog picker on a
 * visit. Returns the visit's stamped `department_id`, EXCEPT it collapses to
 * `null` (→ server returns the full catalog, no scope filter) when the visit
 * has no department (dept-less org / no-dept visit) OR carries the EMERGENCY
 * sentinel. Same falsy-anchor semantics as `filterProvidersByDept` (a falsy
 * anchor is "no filter"), so the procedure picker and the provider picker agree.
 *
 * The server (C1: GET /procedures?department_id=) is the authority — this only
 * decides whether to send the param.
 *
 * @param {object} visit  the visit row ({ department_id, ... })
 * @returns {string|null} the dept anchor, or null for "no filter / full catalog"
 */
export function procedurePickerDeptAnchor(visit) {
  const deptId = visit && visit.department_id;
  if (!deptId || deptId === EMERGENCY_DEPT_SENTINEL) return null;
  return deptId;
}

export function resolveDepartmentPicker({
  departments,
  // S8 — the ACTIVE PROVIDER (doctor OPD / nurse / lab tech). These are the
  // canonical params.
  activeProviderDepts,
  hasActiveProvider,
  // S7 back-compat aliases — earlier this keyed off the DOCTOR only. The S8
  // active-provider args win; the doctor aliases keep straggler callers working.
  selectedDoctorDepts,
  hasDoctor,
  visitType,
  // Legacy alias — even earlier the picker keyed off the registering user's own
  // depts. Lowest precedence.
  memberDepts,
} = {}) {
  // Resolve the effective provider edges + presence flag from whichever arg the
  // caller supplied (active-provider → doctor → member), single-sourced here so
  // every branch below is role-agnostic.
  const providerDepts = activeProviderDepts !== undefined
    ? activeProviderDepts
    : (selectedDoctorDepts !== undefined ? selectedDoctorDepts : memberDepts);
  const hasProvider = hasActiveProvider !== undefined ? hasActiveProvider : hasDoctor;
  const activeDepts = normaliseDepts(departments);
  const hasDepartments = activeDepts.length > 0;
  const isEmergency = isEmergencyVisitType(visitType);

  const NONE = {
    showPicker: false,
    required: false,
    locked: false,
    autoFillDeptId: null,
    options: [],
    isEmergency,
    hasDepartments,
  };

  // Graceful degrade: org without departments behaves exactly like pre-A2. (AC9)
  if (!hasDepartments) return { ...NONE, hasDepartments: false };

  // Emergency/casualty → server stamps the EMERGENCY sentinel; no pick. (AC8)
  if (isEmergency) return { ...NONE, hasDepartments: true, isEmergency: true };

  const activeIds = new Set(activeDepts.map((d) => d.dept_id));
  const providerSelected = hasProvider !== false && hasProvider !== undefined
    ? !!hasProvider
    // hasProvider undefined → infer from presence of a provider-dept list (legacy).
    : Array.isArray(providerDepts);

  // The SELECTED active provider's memberships, restricted to still-ACTIVE org
  // depts (a soft-deleted dept must not auto-fill, lock, or count toward "single").
  const providerEdges = (providerDepts || [])
    .filter((e) => e && activeIds.has(e.dept_id));

  // ── An active provider is selected ──
  if (providerSelected) {
    // Single active dept → auto-fill + LOCK. (AC2)
    if (providerEdges.length === 1) {
      const only = providerEdges[0];
      return {
        showPicker: true,
        required: true,
        locked: true,
        autoFillDeptId: only.dept_id,
        options: [{ dept_id: only.dept_id, name: deptName(activeDepts, only) }],
        isEmergency: false,
        hasDepartments: true,
      };
    }
    // Multiple active depts → CONSTRAIN options to the provider's depts, force pick. (AC3)
    if (providerEdges.length > 1) {
      return {
        showPicker: true,
        required: true,
        locked: false,
        autoFillDeptId: null,
        options: providerEdges.map((e) => ({ dept_id: e.dept_id, name: deptName(activeDepts, e) })),
        isEmergency: false,
        hasDepartments: true,
      };
    }
    // Provider has NO dept → FULL active org list, manual required. (AC4)
    return {
      showPicker: true,
      required: true,
      locked: false,
      autoFillDeptId: null,
      options: activeDepts,
      isEmergency: false,
      hasDepartments: true,
    };
  }

  // ── No active provider selected yet → FULL active org list, manual required. (AC10) ──
  return {
    showPicker: true,
    required: true,
    locked: false,
    autoFillDeptId: null,
    options: activeDepts,
    isEmergency: false,
    hasDepartments: true,
  };
}

/**
 * Reverse filter (AC5): given a chosen department_id and the active-provider list
 * (doctors for OPD, nurses for a Nurse visit, lab techs for a Lab visit), return
 * the providers whose deptsByMember includes that dept. When no dept is chosen the
 * full list is returned unchanged. A provider with an unknown membership (not yet
 * hydrated) is INCLUDED — we never hide a provider because their edges haven't
 * loaded (fail-open); the server is the authority.
 *
 * @param {Array}  providers      the active-provider list — each carries member_id/doctor_id
 * @param {string} departmentId   the chosen dept ('' | null → no filter)
 * @param {object} deptsByMember  member_id → [{dept_id}] map (departmentStore)
 * @returns {Array} filtered providers
 */
export function filterProvidersByDept(providers, departmentId, deptsByMember) {
  const list = Array.isArray(providers) ? providers : [];
  if (!departmentId) return list;
  const map = deptsByMember || {};
  return list.filter((p) => {
    const id = providerId(p);
    const edges = map[id];
    // Not-yet-hydrated edges → keep the provider (fail-open).
    if (edges === undefined) return true;
    // No-department member (loaded, empty list) is ORG-WIDE — available in every
    // department's dropdown (matches the A2 rule: unassigned = visible everywhere).
    // Do NOT hide them when a department is selected.
    if (edges.length === 0) return true;
    // Assigned member: keep only if they belong to the selected department.
    return edges.some((e) => e && e.dept_id === departmentId);
  });
}

// S7 back-compat alias — the OPD doctor filter is the same role-agnostic reverse
// filter. Kept so any straggler caller keeps working.
export const filterDoctorsByDept = filterProvidersByDept;

/**
 * Re-derive the department_id when the active provider changes — a doctor pick,
 * a nurse/lab-tech pick, OR a visit-type switch that changes which provider is
 * active (AC6/AC7, generalized in S8). Given the NEW provider's resolution + the
 * current department_id + whether the current value was a MANUAL (no-provider)
 * pick, decide the next department_id.
 *
 *   - new provider single-dept → reset to that dept (auto-lock). (AC6)
 *   - new provider multi-dept  → KEEP current if it's in the new set, else clear
 *                                + force a pick. (AC6)
 *   - new provider no-dept     → KEEP a valid current value, else clear. (AC6)
 *   - provider CLEARED         → an auto-derived value clears; a MANUAL
 *                                (no-provider) pick is retained if still valid. (AC7)
 *
 * Never keep a department the new provider isn't a member of.
 *
 * @param {object}  args
 * @param {object}  args.resolution        resolveDepartmentPicker(...) for the NEW provider state
 * @param {string}  args.currentDeptId     the currently-selected department_id ('' if none)
 * @param {boolean} args.providerCleared   true when the active provider was just cleared (AC7)
 * @param {boolean} [args.doctorCleared]   S7 back-compat alias for providerCleared.
 * @param {boolean} args.wasManualPick     true when currentDeptId was picked with NO provider set (AC7)
 * @returns {string} the next department_id ('' = cleared / force-pick)
 */
export function rederiveDepartmentOnProviderChange({
  resolution,
  currentDeptId,
  providerCleared,
  doctorCleared,
  wasManualPick,
} = {}) {
  const cleared = providerCleared !== undefined ? providerCleared : doctorCleared;
  const r = resolution || {};
  const cur = currentDeptId || '';

  // Picker not shown (no depts / EMERGENCY) → nothing to keep.
  if (!r.showPicker) return '';

  // Provider cleared → keep a manual (no-provider) pick only if still a valid
  // option; drop anything that was auto-derived from the old provider. (AC7)
  if (cleared) {
    if (wasManualPick && cur && isOption(r.options, cur)) return cur;
    return '';
  }

  // New provider single-dept (locked) → reset to that dept. (AC6)
  if (r.locked && r.autoFillDeptId) return r.autoFillDeptId;

  // New provider multi / no-dept / full-list → keep the current value only when
  // it's a member of the new option set, else clear + force a pick. (AC6)
  if (cur && isOption(r.options, cur)) return cur;
  return '';
}

// S7 back-compat alias — the OPD doctor-change re-derivation is the same
// role-agnostic function. Kept so any straggler caller keeps working.
export const rederiveDepartmentOnDoctorChange = rederiveDepartmentOnProviderChange;

/**
 * Classify a registerVisit error into the provider↔dept-linkage buckets so the
 * form can surface a 422 (re-pick department) distinctly from a 400 (department
 * required). Reads the typed error produced by visitApi.buildApiError.
 *
 * @param {Error} err  the thrown api error (carries .type / .status / .message)
 * @returns {{ kind: 'DEPT_MISMATCH' | 'DEPT_REQUIRED' | 'OTHER', message: string }}
 */
export function classifyRegisterError(err) {
  const status = err?.status ?? err?.response?.status;
  const type = err?.type;
  const message = err?.message || 'Something went wrong.';
  // DEPT_MISMATCH is keyed on TYPE, not bare status — visitApi.buildApiError
  // only assigns this type for a real DeptScopeViolation (string `detail`).
  // A plain 422 status can also mean an unrelated pydantic field-validation
  // error (array `detail`, typed VALIDATION_ERROR there) — matching on
  // status alone here previously mislabeled EVERY 422 as a department
  // mismatch, e.g. a legacy patient record's non-enum gender value.
  if (type === 'DEPT_MISMATCH') {
    return {
      kind: 'DEPT_MISMATCH',
      message: message
        || 'That department isn’t valid for the selected provider. Please re-pick the department.',
    };
  }
  // Register's "department required" failure is a plain 400 (ValueError →
  // HTTPException(400)); buildApiError has no dept-specific type for 400s
  // (shared BAD_REQUEST across endpoints), so status is the only signal here.
  if (status === 400) {
    return { kind: 'DEPT_REQUIRED', message: message || 'Department is required.' };
  }
  return { kind: 'OTHER', message };
}

// ── internals ──────────────────────────────────────────────────────────
function normaliseDepts(departments) {
  return (departments || [])
    .filter((d) => d && d.active !== false)
    .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name }));
}

function deptName(activeDepts, edge) {
  if (edge && edge.name) return edge.name;
  const hit = activeDepts.find((d) => d.dept_id === (edge && edge.dept_id));
  return hit ? hit.name : (edge && edge.dept_id) || '';
}

function isOption(options, deptId) {
  return (options || []).some((o) => (o.dept_id || o.id) === deptId);
}

function providerId(p) {
  return p && (p.member_id || p.doctor_id);
}
